import React, { useState, useEffect } from 'react';
import { HelpCircle, Search, Settings, ChevronDown, Home, RotateCcw, Save, X } from 'lucide-react';

const ProcessDesigner = () => {
    const [view, setView] = useState('list'); // 'list' | 'detail'
    const [selectedProcess, setSelectedProcess] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Filters State
    const [filters, setFilters] = useState({
        status: 'Any',
        trigger: 'All',
        application: 'Any',
        modifiedBy: 'Any',
        modifiedOn: 'All Time'
    });

    // Mock Data State
    const [processes, setProcesses] = useState([
        { id: 1, name: "Process 1", trigger: "On Task Completion", status: "Draft", date: "08/04/2025", by: "System" },
        { id: 2, name: "Initial Call / Follow Up", trigger: "On Task Completion", status: "Published", date: "19/03/2025", by: "System" },
        { id: 3, name: "Sales Team Follow Up", trigger: "On Task Completion", status: "Published", date: "19/03/2025", by: "System" },
        { id: 4, name: "Welcome Call", trigger: "On Task Completion", status: "Published", date: "12/03/2025", by: "System" },
        { id: 5, name: "Booking", trigger: "On Task Completion", status: "Published", date: "12/03/2025", by: "System" },
        { id: 6, name: "Share Lead v1.1", trigger: "At Specific Work Area", status: "Published", date: "12/03/2025", by: "System" },
        { id: 7, name: "Add New Lead", trigger: "At Specific Work Area", status: "Published", date: "10/03/2025", by: "System" },
        { id: 8, name: "Initial Call / Follow Up - At Work Area", trigger: "At Specific Work Area", status: "Published", date: "10/03/2025", by: "System" },
        { id: 9, name: "Share Lead", trigger: "At Specific Work Area", status: "Unpublished", date: "09/03/2025", by: "System" },
        { id: 10, name: "Due Diligence", trigger: "On Task Completion", status: "Published", date: "06/03/2025", by: "System" },
        { id: 11, name: "Document Collection", trigger: "On Task Completion", status: "Published", date: "06/03/2025", by: "System" },
        { id: 12, name: "Demo", trigger: "On Task Completion", status: "Published", date: "06/03/2025", by: "System" },
    ]);

    // Derived Values for Select Options
    const uniqueStatuses = ['Any', ...new Set(processes.map(p => p.status))];
    const uniqueTriggers = ['All', ...new Set(processes.map(p => p.trigger))];
    const uniqueModifiedBy = ['Any', ...new Set(processes.map(p => p.by))];

    // Filtered Data
    const filteredProcesses = processes.filter(p => {
        const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filters.status === 'Any' || p.status === filters.status;
        const matchesTrigger = filters.trigger === 'All' || p.trigger === filters.trigger;
        const matchesModifiedBy = filters.modifiedBy === 'Any' || p.by === filters.modifiedBy;
        return matchesSearch && matchesStatus && matchesTrigger && matchesModifiedBy;
    });

    const handleProcessClick = (process) => {
        setSelectedProcess(process);
        setView('detail');
    };

    const goBack = () => {
        setView('list');
        setSelectedProcess(null);
    };

    const handleUpdateStatus = (id, newStatus) => {
        setProcesses(prev => prev.map(p => p.id === id ? { ...p, status: newStatus } : p));
        // Also update selected process to reflect change immediately in UI if currently viewing
        if (selectedProcess && selectedProcess.id === id) {
            setSelectedProcess(prev => ({ ...prev, status: newStatus }));
        }
    };

    if (view === 'detail') {
        return selectedProcess ?
            <DetailView
                process={ selectedProcess }
                goBack={ goBack }
                onUpdateStatus={ handleUpdateStatus }
            /> : null;
    }

    return (
        <div style={ { fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", color: "#333", paddingBottom: "10px", marginTop: "-15px" } }>
            {/* Header */ }
            <div style={ { padding: "0 0 5px" } }>
                <h1 style={ { fontSize: "20px", fontWeight: "normal", display: "flex", alignItems: "center", gap: "8px", margin: 0 } }>
                    Manage Processes
                    <HelpCircle size={ 16 } color="#999" style={ { cursor: "pointer" } } />
                </h1>
            </div>

            {/* Search Bar */ }
            <div style={ { marginBottom: "10px", display: "flex", gap: "5px" } }>
                <input
                    type="text"
                    placeholder="Search Processes"
                    value={ searchTerm }
                    onChange={ (e) => setSearchTerm(e.target.value) }
                    style={ {
                        padding: "6px 10px",
                        border: "1px solid #ccc",
                        borderRadius: "3px",
                        width: "250px",
                        fontSize: "13px"
                    } }
                />
                <button style={ {
                    padding: "0 10px",
                    background: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: "3px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                } }>
                    <Search size={ 16 } color="#666" />
                </button>
            </div>

            {/* Filter Bar */ }
            <div style={ {
                background: "#f0f0f0",
                border: "1px solid #ddd",
                borderBottom: "none",
                padding: "8px 15px",
                display: "flex",
                alignItems: "center",
                gap: "15px",
                flexWrap: "wrap",
                fontSize: "12px"
            } }>
                <FilterDropdown label="Status" value={ filters.status } options={ uniqueStatuses } onChange={ (val) => setFilters({ ...filters, status: val }) } />
                <FilterDropdown label="Trigger" value={ filters.trigger } options={ uniqueTriggers } onChange={ (val) => setFilters({ ...filters, trigger: val }) } />
                <FilterDropdown label="Application" value={ filters.application } options={ ['Any'] } onChange={ (val) => setFilters({ ...filters, application: val }) } />
                <FilterDropdown label="Modified By" value={ filters.modifiedBy } options={ uniqueModifiedBy } onChange={ (val) => setFilters({ ...filters, modifiedBy: val }) } />
                <FilterDropdown label="Modified On" value={ filters.modifiedOn } options={ ['All Time', 'Today', 'Yesterday'] } onChange={ (val) => setFilters({ ...filters, modifiedOn: val }) } />
            </div>

            {/* Table */ }
            <div style={ { border: "1px solid #ddd", background: "#fff" } }>
                <table style={ { width: "100%", borderCollapse: "collapse", fontSize: "13px" } }>
                    <thead>
                        <tr style={ { background: "#f0f0f0", borderBottom: "1px solid #ddd", textAlign: "left", color: "#333", fontWeight: "bold" } }>
                            <th style={ { padding: "8px 15px", width: "30%" } }>Name</th>
                            <th style={ { padding: "8px 15px", width: "20%" } }>Trigger Type</th>
                            <th style={ { padding: "8px 15px", width: "10%" } }>Status</th>
                            <th style={ { padding: "8px 15px", width: "15%" } }>Modified On <ChevronDown size={ 12 } style={ { display: "inline", verticalAlign: "middle" } } /></th>
                            <th style={ { padding: "8px 15px", width: "15%" } }>Modified By</th>
                            <th style={ { padding: "8px 15px", width: "10%", textAlign: "center" } }>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        { filteredProcesses.map((p, i) => (
                            <tr key={ p.id } style={ { borderBottom: "1px solid #eee", background: i % 2 === 0 ? "#fff" : "#fbfbfb" } }>
                                <td style={ { padding: "10px 15px" } }>
                                    <span
                                        onClick={ () => handleProcessClick(p) }
                                        style={ { color: "#2563eb", cursor: "pointer", fontWeight: 500, textDecoration: "none" } }
                                        onMouseEnter={ (e) => e.target.style.textDecoration = "underline" }
                                        onMouseLeave={ (e) => e.target.style.textDecoration = "none" }
                                    >
                                        { p.name }
                                    </span>
                                </td>
                                <td style={ { padding: "10px 15px" } }>
                                    <span style={ { borderBottom: "1px dashed #999", cursor: "help" } }>{ p.trigger }</span>
                                </td>
                                <td style={ { padding: "10px 15px", color: "#444" } }>{ p.status }</td>
                                <td style={ { padding: "10px 15px", color: "#444" } }>{ p.date }</td>
                                <td style={ { padding: "10px 15px", color: "#444" } }>{ p.by }</td>
                                <td style={ { padding: "10px 15px", textAlign: "center" } }>
                                    <Settings size={ 16 } color="#888" style={ { cursor: "pointer" } } />
                                </td>
                            </tr>
                        )) }
                        { filteredProcesses.length === 0 && (
                            <tr>
                                <td colSpan={ 6 } style={ { padding: "20px", textAlign: "center", color: "#888" } }>
                                    No processes found.
                                </td>
                            </tr>
                        ) }
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// --- Sub Components ---

const FilterDropdown = ({ label, value, options, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div style={ { display: "flex", alignItems: "center", gap: "5px", position: "relative" } }>
            <span style={ { fontWeight: "bold", color: "#555" } }>{ label }</span>
            <div
                onClick={ () => setIsOpen(!isOpen) }
                style={ {
                    background: "#fff",
                    border: "1px solid #ccc",
                    padding: "3px 8px",
                    borderRadius: "3px",
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    fontSize: "12px",
                    minWidth: "80px",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    userSelect: "none"
                } }
            >
                { value } <ChevronDown size={ 12 } color="#666" />
            </div>

            { isOpen && (
                <>
                    <div
                        style={ { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 10 } }
                        onClick={ () => setIsOpen(false) }
                    />
                    <div style={ {
                        position: "absolute",
                        top: "100%",
                        left: "30px",
                        marginTop: "2px",
                        background: "#fff",
                        border: "1px solid #ccc",
                        borderRadius: "3px",
                        boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                        zIndex: 20,
                        minWidth: "120px",
                        maxHeight: "200px",
                        overflowY: "auto"
                    } }>
                        { options.map((opt, idx) => (
                            <div
                                key={ idx }
                                onClick={ () => { onChange(opt); setIsOpen(false); } }
                                style={ {
                                    padding: "6px 12px",
                                    cursor: "pointer",
                                    fontSize: "12px",
                                    background: value === opt ? "#eff6ff" : "transparent",
                                    color: value === opt ? "#2563eb" : "#333"
                                } }
                                onMouseEnter={ (e) => { if (value !== opt) e.target.style.background = "#f5f5f5"; } }
                                onMouseLeave={ (e) => { if (value !== opt) e.target.style.background = "transparent"; } }
                            >
                                { opt }
                            </div>
                        )) }
                    </div>
                </>
            ) }
        </div>
    );
};

const DetailView = ({ process, goBack, onUpdateStatus }) => {
    const isLive = process.status === "Published";

    // Standard 4-node flow reused by multiple processes
    const isStandardFlow = [
        "Process 1",
        "Initial Call / Follow Up",
        "Sales Team Follow Up",
        "Welcome Call",
        "Booking",
        "Due Diligence",
        "Document Collection",
        "Demo"
    ].includes(process.name);

    return (
        <div style={ { display: "flex", flexDirection: "column", height: "calc(100vh - 120px)", background: "#fafafa", position: "relative" } }>
            {/* Top Toolbar */}
            <div style={{ 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "space-between", 
                padding: "2px 20px", 
                background: "#fff", 
                borderBottom: "1px solid #eee",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
            }}>
                <div style={ { display: "flex", alignItems: "center", gap: "10px" } }>
                    <div
                        onClick={ goBack }
                        style={ {
                            padding: "6px",
                            border: "1px solid #e5e7eb",
                            borderRadius: "4px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            color: "#6b7280"
                        } }
                    >
                        <Home size={ 18 } />
                    </div>

                    <div style={ {
                        display: "flex",
                        alignItems: "center",
                        border: "1px solid #e5e7eb",
                        borderRadius: "4px",
                        padding: "6px 12px",
                        background: "#fff",
                        minWidth: "200px"
                    } }>
                        { isLive ? (
                            <div style={ { width: "8px", height: "8px", background: "#4ade80", borderRadius: "50%", marginRight: "8px" } }></div>
                        ) : (
                            <div style={ { width: "8px", height: "8px", background: "#d1d5db", borderRadius: "50%", marginRight: "8px" } }></div>
                        ) }
                        <span style={ { fontSize: "14px", fontWeight: 500, color: "#374151" } }>{ process.name }</span>
                    </div>

                    <div style={ {
                        padding: "6px",
                        border: "1px solid #e5e7eb",
                        borderRadius: "4px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        color: "#6b7280"
                    } }>
                        <Settings size={ 18 } />
                    </div>
                </div>

                <div style={ { display: "flex", alignItems: "center", gap: "12px" } }>
                    { isLive ? (
                        <>
                            <button style={ {
                                padding: "6px 16px",
                                background: "#f97316",
                                color: "#fff",
                                border: "none",
                                borderRadius: "4px",
                                fontSize: "13px",
                                fontWeight: 500,
                                cursor: "pointer"
                            } }>
                                Live Edit
                            </button>
                            <button
                                onClick={ () => onUpdateStatus(process.id, "Draft") }
                                style={ {
                                    padding: "6px 16px",
                                    background: "#3b82f6",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "4px",
                                    fontSize: "13px",
                                    fontWeight: 500,
                                    cursor: "pointer"
                                } }>
                                Unpublish
                            </button>
                        </>
                    ) : (
                        <>
                            <div style={ { display: "flex", border: "1px solid #eee", borderRadius: "4px", overflow: "hidden" } }>
                                <button style={ { padding: "6px 10px", background: "#fff", border: "none", borderRight: "1px solid #eee", cursor: "pointer", color: "#666" } }><RotateCcw size={ 16 } /></button>
                                <button style={ { padding: "6px 10px", background: "#fff", border: "none", borderRight: "1px solid #eee", cursor: "pointer", color: "#666" } }><RotateCcw size={ 16 } style={ { transform: "scaleX(-1)" } } /></button>
                                <button style={ { padding: "6px 10px", background: "#fff", border: "none", cursor: "pointer", color: "#666" } }><X size={ 16 } /></button>
                            </div>
                            <button
                                onClick={ () => onUpdateStatus(process.id, process.status) }
                                style={ {
                                    padding: "6px 16px",
                                    background: "#fff",
                                    color: "#374151",
                                    border: "1px solid #d1d5db",
                                    borderRadius: "4px",
                                    fontSize: "13px",
                                    fontWeight: 500,
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px"
                                } }>
                                <Save size={ 14 } /> Save
                            </button>
                            <button
                                onClick={ () => onUpdateStatus(process.id, "Published") }
                                style={ {
                                    padding: "6px 16px",
                                    background: "#3b82f6",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "4px",
                                    fontSize: "13px",
                                    fontWeight: 500,
                                    cursor: "pointer"
                                } }>
                                Publish
                            </button>
                        </>
                    ) }
                </div>
            </div>

            {/* Canvas Area */ }
            <div style={ {
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "40px",
                overflowY: "auto",
                background: "url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjEiIGZpbGw9IiNlNWU3ZWIiLz48L3N2Zz4=')" // Dot grid pattern
            } }>
                <div style={ { marginBottom: "20px", fontSize: "12px", color: "#6b7280", fontWeight: 500 } }>Your Process starts here</div>

                { isStandardFlow && (
                    <StandardFlow processName={ process.name } />
                ) }

                { process.name === "Share Lead v1.1" && (
                    <ShareLeadV1_1 />
                ) }

                { process.name === "Add New Lead" && (
                    <div style={ { display: 'flex', flexDirection: 'column', alignItems: 'center' } }>
                        <ProcessNode
                            title="At Specific Work Area"
                            subtitle="Edit, Edit, Edit, Add New Lead, Add..."
                            type="trigger"
                            borderColor="#2563eb"
                            label="Event1"
                        />
                        <Arrow />
                        <ProcessNode
                            title="Show Form"
                            subtitle="Add New Lead"
                            type="action"
                            borderColor="#60a5fa"
                            label="ShowForm2"
                        />
                    </div>
                ) }

                { process.name === "Initial Call / Follow Up - At Work Area" && (
                    <div style={ { display: 'flex', flexDirection: 'column', alignItems: 'center' } }>
                        <ProcessNode
                            title="At Specific Work Area"
                            subtitle="Add Activity, Add Activity, Add..."
                            type="trigger"
                            borderColor="#2563eb"
                            label="Event1"
                        />
                        <Arrow />
                        <ProcessNode
                            title="Show Form"
                            subtitle="Initial Call / Follow-Up"
                            type="action"
                            borderColor="#60a5fa"
                            label="ShowForm2"
                        />
                    </div>
                ) }

                { process.name === "Share Lead" && (
                    <div style={ { display: 'flex', flexDirection: 'column', alignItems: 'center' } }>
                        <ProcessNode
                            title="At Specific Work Area"
                            subtitle="Add Activity, Add Activity, Add..."
                            type="trigger"
                            borderColor="#2563eb"
                            label="Event2"
                        />
                        <Arrow />
                        <ProcessNode
                            title="Show Form"
                            subtitle="Share Lead"
                            type="action"
                            borderColor="#60a5fa"
                            label="ShowForm3"
                        />
                        <PlusButton />
                    </div>
                ) }
            </div>
        </div>
    );
};

// --- Custom Flow Components ---

const StandardFlow = ({ processName }) => (
    <div style={ { display: 'flex', flexDirection: 'column', alignItems: 'center' } }>
        <ProcessNode
            title="On Task Complete"
            subtitle={ `Follow Up - Callback, ${processName}...` }
            type="trigger"
            borderColor="#2563eb"
            label="Event1"
        />
        <Arrow />
        <ProcessNode
            title="Show Form"
            subtitle={ processName }
            type="action"
            borderColor="#60a5fa"
            label="ShowForm2"
        />
        <Arrow />
        <ProcessNode
            title="Wait Until Save"
            subtitle={ processName }
            type="wait"
            borderColor="#f59e0b"
            label="WaitUntilSave3"
        />
        <Arrow />
        <ProcessNode
            title="Mark Task Complete"
            subtitle={ `Follow Up - Callback, ${processName}...` }
            type="action"
            borderColor="#60a5fa"
            label="MarkTaskComplete4"
        />
    </div>
);

const ShareLeadV1_1 = () => (
    <div style={ { display: 'flex', flexDirection: 'column', alignItems: 'center' } }>
        <ProcessNode
            title="At Specific Work Area"
            subtitle="Add Activity, Add Activity, Add..."
            type="trigger"
            borderColor="#2563eb"
            label="Event2"
        />
        <Arrow />
        <ProcessNode
            title="Multi If/Else User"
            subtitle="IF(expression) THEN A ELSE..."
            type="action"
            borderColor="#a855f7" // Purple
            label="MultiIfElseUser4"
        />
        <BranchLines />
        <div style={ { display: "flex", gap: "20px", alignItems: "flex-start" } }>
            {/* Left Branch */ }
            <div style={ { display: "flex", flexDirection: "column", alignItems: "center" } }>
                <ProcessNode
                    title="If User"
                    subtitle="If user matches the configured..."
                    type="action"
                    borderColor="#22c55e" // Green
                    label="IfUser5"
                />
                <Arrow />
                <ProcessNode
                    title="Show Form"
                    subtitle="Share Lead"
                    type="action"
                    borderColor="#60a5fa"
                    label="ShowForm3"
                />
            </div>

            {/* Right Branch */ }
            <div style={ { display: "flex", flexDirection: "column", alignItems: "center" } }>
                <ProcessNode
                    title="Else User"
                    subtitle="Else User"
                    type="action"
                    borderColor="#ef4444" // Red
                    label="ElseUser6"
                />
            </div>
        </div>
    </div>
);

// --- Graphics Components ---

const BranchLines = () => (
    <div style={ { width: "320px", height: "30px", position: "relative" } }>
        {/* Center Vertical */ }
        <div style={ { position: "absolute", top: 0, left: "50%", height: "15px", width: "1px", background: "#9ca3af" } }></div>
        {/* Horizontal Bar */ }
        <div style={ { position: "absolute", top: "15px", left: "25%", right: "25%", height: "1px", background: "#9ca3af" } }></div>
        {/* Left Vertical Down */ }
        <div style={ { position: "absolute", top: "15px", left: "25%", height: "15px", width: "1px", background: "#9ca3af" } }></div>
        <div style={ { position: "absolute", bottom: "-4px", left: "24.7%", width: "0", height: "0", borderLeft: "4px solid transparent", borderRight: "4px solid transparent", borderTop: "4px solid #9ca3af" } }></div>
        {/* Right Vertical Down */ }
        <div style={ { position: "absolute", top: "15px", right: "25%", height: "15px", width: "1px", background: "#9ca3af" } }></div>
        <div style={ { position: "absolute", bottom: "-4px", right: "24.7%", width: "0", height: "0", borderLeft: "4px solid transparent", borderRight: "4px solid transparent", borderTop: "4px solid #9ca3af" } }></div>
    </div>
);

const PlusButton = () => (
    <div style={ {
        marginTop: "10px",
        width: "24px",
        height: "24px",
        borderRadius: "50%",
        border: "1px solid #60a5fa",
        color: "#60a5fa",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        background: "#fff"
    } }>
        <div style={ { fontSize: "18px", fontWeight: "bold", lineHeight: 1 } }>+</div>
    </div>
);

const ProcessNode = ({ title, subtitle, type, borderColor, label }) => (
    <div style={ { position: 'relative', marginBottom: type === 'trigger' ? 0 : 0 } }>
        <div style={ {
            width: "280px",
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: "4px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
            position: "relative",
            textAlign: "left",
            overflow: "hidden",
            zIndex: 2
        } }>
            <div style={ { padding: "12px 16px" } }>
                <div style={ { fontSize: "14px", fontWeight: 600, color: "#374151" } }>{ title }</div>
                <div style={ { fontSize: "11px", color: "#9ca3af", marginTop: "4px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" } }>{ subtitle }</div>
            </div>
            {/* Type Indicator Line */ }
            <div style={ { height: "4px", background: borderColor, width: "100%" } }></div>
        </div>
        {/* Label below */ }
        <div style={ {
            position: "absolute",
            top: "100%",
            left: "0",
            fontSize: "10px",
            color: "#9ca3af",
            marginTop: "2px"
        } }>
            { label }
        </div>
    </div>
);

const Arrow = () => (
    <div style={ { display: "flex", flexDirection: "column", alignItems: "center", height: "40px", justifyContent: "center" } }>
        <div style={ { width: "1px", height: "100%", background: "#d1d5db" } }></div>
        <div style={ { width: "0", height: "0", borderLeft: "4px solid transparent", borderRight: "4px solid transparent", borderTop: "4px solid #d1d5db", marginBottom: "-4px" } }></div>
    </div>
);

export default ProcessDesigner;
